/**
 * cart-goals.js — Fizz Drawer (Modal) kompatibel
 * - Initialisiert erst, wenn [data-cart-goals] in den DOM teleportiert wurde
 * - Beobachtet den DOM (MutationObserver) und Drawer-Events
 * - Verhindert Mehrfach-Init pro Instanz via data-goals-initialized
 * - Interceptet /cart/*.js Aufrufe und aktualisiert Progress + Gift
 */

(function () {
  console.log('[cart-goals] script loaded');

  // === Konfiguration ===
  const SELECTOR_GOALS = '[data-cart-goals]';
  const EVENT_FORCE_UPDATE = 'cart-goals:force-update';
  const EVENT_DRAWER_OPEN = 'msb:drawer:open';   // optional – falls du so ein Event selbst triggert
  const EVENT_DRAWER_CLOSE = 'msb:drawer:close'; // optional
  const HEARTBEAT_MS = 600;

  // Verhindere mehrfaches Setzen des globalen fetch-intercepts
  if (!window.__cartGoalsFetchPatched) {
    const _fetch = window.fetch;
    window.fetch = async function (input, init) {
      const isCart =
        typeof input === 'string' &&
        /\/cart\/(add|change|update)\.js/.test(input);
      const res = await _fetch.apply(this, arguments);
      if (isCart) {
        // Warten bis Shopify neu berechnet hat
        try { await res.clone().json(); } catch (_e) {}
        setTimeout(() => {
          document.querySelectorAll(SELECTOR_GOALS).forEach((el) => {
            if (el.__cartGoalsInstance) el.__cartGoalsInstance.update();
          });
          // Fallback-Trigger für externe Listener
          window.dispatchEvent(new CustomEvent('cart:updated'));
        }, 150);
      }
      return res;
    };
    window.__cartGoalsFetchPatched = true;
  }

  // Hilfsfunktionen
  const euroFmt = (currency) => (n) =>
    (Number(n).toFixed(2).replace('.', ',')) + (currency || '€');

  async function getCart() {
    const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return await r.json();
  }

  // Eine Instanz pro Goals-Element
  function attachCartGoals(el) {
    if (!el || el.dataset.goalsInitialized === 'true') return;
    el.dataset.goalsInitialized = 'true';

    const tGift = parseFloat(el.dataset.thresholdGift || 10);
    const tShip = parseFloat(el.dataset.thresholdShip || 30);
    const t10   = parseFloat(el.dataset.threshold10  || 50);
    const t15   = parseFloat(el.dataset.threshold15  || 100);
    const giftVariantId = el.dataset.giftVariantId;
    const currency = el.dataset.currencySymbol || '€';
    const fmt = euroFmt(currency);

    const textEl = el.querySelector('[data-goal-text]');
    const fillEl = el.querySelector('[data-goal-fill]');
    const stackEl = el.querySelector('[data-goal-stack]');

    const goals = [
      { key: 'gift', label: 'Gratis Fidget',         threshold: tGift },
      { key: 'ship', label: 'Kostenloser Versand',   threshold: tShip },
      { key: '10',   label: '10% Rabatt',            threshold: t10   },
      { key: '15',   label: '15% Rabatt',            threshold: t15   },
    ];

    function nextGoal(amount) {
      if (amount < tGift) return { to: tGift, label: `Noch ${fmt(tGift - amount)} für ein Gratis Fidget! 🥳` };
      if (amount < tShip) return { to: tShip, label: `Noch ${fmt(tShip - amount)} für kostenlosen Versand! 🥳` };
      if (amount < t10)   return { to: t10,  label: `Noch ${fmt(t10  - amount)} für 10% Rabatt! 🥳` };
      if (amount < t15)   return { to: t15,  label: `Noch ${fmt(t15  - amount)} für 15% Rabatt! 🥳` };
      return { to: t15, label: 'Alle Boni erreicht! 🎉' };
    }

    function stageProgress(amount) {
      if (amount < tGift) return (amount / tGift) * 100;
      if (amount < tShip) return ((amount - tGift) / (tShip - tGift)) * 100;
      if (amount < t10)   return ((amount - tShip) / (t10 - tShip)) * 100;
      if (amount < t15)   return ((amount - t10)  / (t15 - t10))   * 100;
      return 100;
    }

    function renderStack(amount) {
      if (!stackEl) return;
      stackEl.querySelectorAll('.goal').forEach((li) => {
        const key = li.dataset.goal;
        const g = goals.find((x) => x.key === key);
        if (!g) return;
        if (amount >= g.threshold) {
          li.classList.add('goal--earned');
          const ico = li.querySelector('.goal__icon');
          if (ico) ico.textContent = '✅';
        } else {
          li.classList.remove('goal--earned');
          const ico = li.querySelector('.goal__icon');
          if (ico) ico.textContent = '⬤';
        }
      });
    }

    function hasGift(cart) {
      if (!giftVariantId) return false;
      return (cart.items || []).some(
        (i) => String(i.variant_id) === String(giftVariantId)
      );
    }

    async function addGift() {
      if (!giftVariantId) return;
      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(giftVariantId), quantity: 1, properties: { _gift: true } }],
          }),
        });
      } catch (e) {
        console.warn('[cart-goals] Gift add failed', e);
      }
    }

    async function removeGift(line) {
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity: 0 }),
        });
      } catch (e) {
        console.warn('[cart-goals] Gift remove failed', e);
      }
    }

    async function syncGift(cart, amount) {
      if (!giftVariantId) return;
      const present = hasGift(cart);
      if (amount >= tGift && !present) {
        await addGift();
      } else if (amount < tGift && present) {
        const idx = (cart.items || []).findIndex(
          (i) => String(i.variant_id) === String(giftVariantId)
        );
        if (idx > -1) await removeGift(idx + 1); // line = index+1
      }
    }

    async function update() {
      try {
        const cart = await getCart();
        const amount = (cart.items_subtotal_price || 0) / 100;
        const next = nextGoal(amount);
        const pct = Math.max(0, Math.min(100, stageProgress(amount)));

        if (textEl) textEl.textContent = next.label;
        if (fillEl) fillEl.style.width = pct + '%';
        renderStack(amount);
        await syncGift(cart, amount);

        window.dispatchEvent(
          new CustomEvent('cart-goals:updated', { detail: { amount, next, pct } })
        );
      } catch (e) {
        console.warn('[cart-goals] update failed', e);
      }
    }

    // Instanz-API auf Element speichern (für externe Trigger)
    el.__cartGoalsInstance = { update };

    // Sofort initial updaten & Heartbeat
    update();
    const timer = setInterval(update, HEARTBEAT_MS);

    // Aufräumen, falls der Drawer-Inhalt entfernt wird
    const destroy = () => {
      clearInterval(timer);
      el.__cartGoalsInstance = null;
      el.removeAttribute('data-goals-initialized');
    };

    // Beobachte, ob das Element aus dem DOM verschwindet (z. B. Drawer schließt)
    const ro = new MutationObserver(() => {
      if (!document.documentElement.contains(el)) {
        destroy();
        ro.disconnect();
      }
    });
    ro.observe(document.documentElement, { childList: true, subtree: true });

    // Externe Events (optional)
    window.addEventListener(EVENT_FORCE_UPDATE, () => update());
    window.addEventListener(EVENT_DRAWER_OPEN, () => update());
    window.addEventListener(EVENT_DRAWER_CLOSE, () => update());
  }

  // Init-Strategie:
  // 1) Sofort versuchen (Cart-Seite serverseitig)
  function tryInitIn(doc) {
    const root = doc || document;
    root.querySelectorAll(SELECTOR_GOALS).forEach(attachCartGoals);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => tryInitIn(document));
  } else {
    tryInitIn(document);
  }

  // 2) MutationObserver: erkennt Teleport/Drawer-Inhalte
  const mo = new MutationObserver((muts) => {
    for (const m of muts) {
      if (m.type === 'childList' && m.addedNodes.length) {
        m.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches(SELECTOR_GOALS)) attachCartGoals(node);
          if (node.querySelector) {
            const found = node.querySelector(SELECTOR_GOALS);
            if (found) attachCartGoals(found);
          }
        });
      }
    }
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });

  // 3) Häufige Theme-Events mitnehmen
  window.addEventListener('shopify:section:load', () => tryInitIn(document));
  window.addEventListener('cart:updated', () => tryInitIn(document));
  // Alpine/Drawer: in deinem drawer-cart.liquid kannst du zusätzlich setzen:
  // x-init="() => { window.dispatchEvent(new Event('cart-goals:force-update')) }"

})();
